import Phaser from 'phaser'
import { hasMsg, type JKGameMsgKey, t } from '../../i18n'
import { apiLeaderboard, getHandle, type LeaderboardMode, type LeaderboardResult, type LeaderboardRow, type RankTier } from '../../services/JKGame_economy'
import { mountLeaderboardOverlay, type LeaderboardEntryRow, type LeaderboardOverlayHandle } from '../../ui/JKGame_leaderboardOverlay'
import { audio } from '../JKGame_audio'
import { COLORS } from '../JKGame_ui'

const TIER_COLOR: Record<RankTier, string> = {
  bronze: '#cd7f32',
  silver: '#c0c7d0',
  gold: '#ffd84d',
  platinum: '#7fd0ff',
  diamond: '#c9a3ff',
  master: '#5ee68a',
}

function tierName(tier: RankTier): string {
  const k = `tier.${tier}`
  return hasMsg(k) ? t(k as JKGameMsgKey) : tier
}

// Ranking is the visible proof that Daily Ranked is async PvP, not just another solo mode.
// Backend remains the source of truth for points, tier, wins, and the player's own standing.
export class JKGame_LeaderboardScene extends Phaser.Scene {
  private alive = true
  private mode: LeaderboardMode = 'casual'
  private overlay?: LeaderboardOverlayHandle
  private requestSeq = 0
  private readonly keyEsc = (): void => {
    this.scene.start('Progress')
  }

  constructor() {
    super('Leaderboard')
  }

  create(): void {
    this.alive = true
    this.requestSeq = 0
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.alive = false
      this.overlay?.destroy()
      this.overlay = undefined
      this.input.keyboard?.off('keydown-ESC', this.keyEsc)
    })
    this.input.keyboard?.off('keydown-ESC', this.keyEsc).on('keydown-ESC', this.keyEsc)
    this.cameras.main.setBackgroundColor(COLORS.bg)
    this.cameras.main.fadeIn(240, 0, 0, 0)

    this.overlay = mountLeaderboardOverlay({
      title: t('leaderboard.title'),
      pitch: t('leaderboard.pitch'),
      loadingText: t('menu.loading'),
      backLabel: t('menu.back'),
      activeMode: this.mode,
      modes: [
        { id: 'casual', label: t('leaderboard.casual') },
        { id: 'ranked', label: t('leaderboard.ranked') },
      ],
      onMode: (mode) => {
        audio.play('uiTap')
        this.switchMode(mode)
      },
      onBack: () => {
        audio.play('uiTap')
        this.scene.start('Progress')
      },
    })

    void this.refresh()
  }

  private switchMode(mode: LeaderboardMode): void {
    if (mode === this.mode) return
    this.mode = mode
    this.overlay?.setMode(mode)
    this.overlay?.setStatus(t('menu.loading'))
    void this.refresh()
  }

  private async refresh(): Promise<void> {
    const seq = ++this.requestSeq
    try {
      const board = await apiLeaderboard(getHandle(), 10, this.mode)
      if (!this.alive || seq !== this.requestSeq) return
      this.render(board)
    } catch {
      if (!this.alive || seq !== this.requestSeq) return
      this.overlay?.render('', [], '')
      this.overlay?.setStatus(t('leaderboard.offline'), 'bad')
    }
  }

  private render(board: LeaderboardResult): void {
    if (board.entries.length === 0) {
      this.overlay?.render(t('leaderboard.season', { id: board.season_id }), [], this.meLine(board.me))
      this.overlay?.setStatus(t('leaderboard.empty'))
      return
    }

    const me = getHandle()
    const rows: LeaderboardEntryRow[] = board.entries.map((entry) => {
      const isMe = entry.handle === me
      return {
        rank: entry.rank,
        medal: entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : `#${entry.rank}`,
        name: this.fmtName(entry, isMe),
        points: t('leaderboard.points', { n: entry.points }),
        wins: entry.wins > 0 ? `🏆${entry.wins}` : '',
        tierName: entry.tier ? tierName(entry.tier) : '',
        tierColor: entry.tier ? TIER_COLOR[entry.tier] : '',
        isMe,
      }
    })
    this.overlay?.render(t('leaderboard.season', { id: board.season_id }), rows, this.meLine(board.me))
  }

  private meLine(meRow: LeaderboardRow | null): string {
    return meRow
      ? t('leaderboard.me', { rank: meRow.rank, points: meRow.points }) + (meRow.tier ? `　◈ ${tierName(meRow.tier)}` : '')
      : t('leaderboard.meNone')
  }

  private fmtName(entry: LeaderboardRow, isMe: boolean): string {
    return `${entry.handle}${isMe ? `（${t('leaderboard.you')}）` : ''}`
  }
}
